import React from 'react';
import MUIICon from '@mui/material/Icon';

import {useAriaAndDataProps} from '@famly/mf_ts-utils_data-aria-props';
import {MATERIAL_SYMBOLS_FULL_FONT_FAMILY, useTheme} from '@famly/mf_theme';
import {useTooltipRequiredProps} from '@famly/mf_data-display_tooltip';

import type {IconName} from './icon.type';
import {CustomIcon} from './custom-icon';
import {isCustomIconName} from './custom-icon-registry';
import type {CustomIconName} from './custom-icon.type';
import {isInMaterialSymbolsSubset} from './material-symbols-subset/is-in-material-symbols-subset';

export type IconProps = {
    /**
     * A Material Symbols icon name, or the name of one of Modern Famly's custom SVG icons.
     *
     * The component decides based on the name alone whether to render the Material Symbols font
     * icon or the (lazily loaded) custom SVG — so consumers never need to special-case custom
     * icons.
     */
    name: IconName | CustomIconName;
    /**
     * The color of the icon
     */
    color?: ColorKey;
    /**
     * Whether the icon should be filled or not
     */
    filled?: boolean;
    /**
     * The size of the icon.
     */
    size?: number;
    /**
     * Ref forwarded to the underlying icon element.
     */
    ref?: React.Ref<HTMLSpanElement>;
};

export const Icon = ({name, color: colorFromProps, filled, size, ref, ...props}: IconProps) => {
    const theme = useTheme();
    const dataAriaProps = useAriaAndDataProps(props);
    const tooltipRequiredProps = useTooltipRequiredProps(props);

    const resolvedColor = colorFromProps ? theme.colorPalette[colorFromProps] : undefined;
    const color = resolvedColor ?? 'inherit';

    const sx = {
        fontVariationSettings: filled
            ? `
        'FILL' 1,
        'wght' 300,
        'GRAD' 0,
        'opsz' 24
    `
            : undefined,
        fontSize: size ? `${size}px` : undefined,
        color,
        // Adding these styles to make the icon work better with tooltips
        display: 'inline-flex',
        cursor: 'inherit',
        lineHeight: 1,
    };

    if (isCustomIconName(name)) {
        return (
            <CustomIcon
                name={name}
                size={size}
                // SVGs can't resolve `inherit`, so fall back to `currentColor` to inherit the text color.
                color={resolvedColor ?? 'currentColor'}
                filled={filled}
                sx={sx}
            />
        );
    }

    // The theme renders icons in the subset font, which has no glyph for a name the code never writes
    // out, such as one from the backend. `MATERIAL_SYMBOLS_FONT_STACK` says why it cannot fall back.
    const fontFamily = isInMaterialSymbolsSubset(name) ? undefined : MATERIAL_SYMBOLS_FULL_FONT_FAMILY;

    return (
        <MUIICon ref={ref} sx={{...sx, fontFamily}} {...dataAriaProps} {...tooltipRequiredProps}>
            {name}
        </MUIICon>
    );
};

Icon.displayName = 'Icon';
